import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Box, Grid2, MenuItem, TextField } from "@mui/material";
import { type FormEvent, useEffect, useState } from "react";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	useBuildRecipe,
	useRecipesMeta,
} from "../hooks/useInformationRetrieval";

interface Props {
	open: boolean;
	productSizeId: number | null;
	start: string;
	end: string;
	onHide: (created: boolean) => void;
}

export const BuildRecipeModal = ({
	open,
	productSizeId,
	start,
	end,
	onHide,
}: Props) => {
	const [recipeId, setRecipeId] = useState<number | null>(null);
	const [name, setName] = useState("");
	const [limitsPercentage, setLimitsPercentage] = useState("");
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const recipesQuery = useRecipesMeta();
	const buildMutation = useBuildRecipe();

	useEffect(() => {
		if (open) {
			setRecipeId(null);
			setName("");
			setLimitsPercentage("");
		}
	}, [open]);

	const recipesForProduct = (recipesQuery.data ?? []).filter(
		(r) => productSizeId == null || r.ProductSizeId === productSizeId,
	);

	// Either overwrite an existing recipe or name a new one — never both, which
	// is how the legacy modal gates its Accept button.
	const hasTarget = recipeId != null || name.trim() !== "";

	/**
	 * Limits is required here even though the legacy modal lets it through. The
	 * procedure writes `RFactorMin`/`RFactorMax` as NULL when the percentage is
	 * missing, and as target ± 0 when it is zero — a recipe with no tolerance
	 * band, or one so tight every billet reads out of limits. Neither is usable,
	 * and neither tells the operator anything went wrong.
	 */
	const limitsValue = Number(limitsPercentage);
	const isLimitsValid =
		limitsPercentage.trim() !== "" &&
		Number.isFinite(limitsValue) &&
		limitsValue > 0 &&
		limitsValue <= 100;

	const canSubmit = hasTarget && isLimitsValid;

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		if (!canSubmit || productSizeId == null) return;

		buildMutation.mutate(
			{
				productSizeId,
				start,
				end,
				recipeId,
				name: name.trim(),
				limitsPercentage: limitsValue,
			},
			{
				onSuccess: () => {
					setNotificationMessage("The recipe was created successfully");
					onHide(true);
				},
				onError: (err: unknown) => setError((err as Error).message),
			},
		);
	};

	return (
		<>
			<MesfModal
				id="modal-build-recipe"
				title="Create recipe from billets data"
				open={open}
				handleClose={() => onHide(false)}
				maxWidth="sm"
			>
				{/* FooterModalButtons renders its confirm button as type="submit"
				    with no click handler, so it is inert outside a form. */}
				<form onSubmit={handleSubmit} noValidate>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						{/* Helper lines start at the field's own left border, not at the
						    text inset MUI defaults to — otherwise the hint sits adrift
						    from the box it belongs to. */}
						<Grid2
							container
							spacing={2}
							sx={{
								"& .MuiFormHelperText-root": {
									marginLeft: 0,
									marginRight: 0,
								},
							}}
						>
							<Grid2 size={12}>
								<TextField
									select
									label="Recipe"
									size="small"
									fullWidth
									// Without displayEmpty the closed box renders blank for the
									// empty value, so "Create a new recipe" — the default —
									// looks like nothing was chosen at all.
									slotProps={{
										select: { displayEmpty: true },
										inputLabel: { shrink: true },
									}}
									helperText={
										recipeId == null
											? "A new recipe will be created under the name below"
											: "This recipe will be overwritten with the measured values"
									}
									value={recipeId == null ? "" : String(recipeId)}
									onChange={(e) => {
										const v = e.target.value;
										setRecipeId(v === "" ? null : Number(v));
										if (v !== "") setName("");
									}}
								>
									<MenuItem value="">Create a new recipe</MenuItem>
									{recipesForProduct.map((r) => (
										<MenuItem key={r.Id} value={String(r.Id)}>
											{`${r.ProductSizeName} ${r.ProductTypeName} ${r.Name}`}
										</MenuItem>
									))}
								</TextField>
							</Grid2>

							{/* Naming only applies to a new recipe. Picking an existing one
						    overwrites it, so the legacy modal hides this field rather
						    than greying it — a disabled box just looks broken. */}
							{recipeId == null ? (
								<Grid2 size={12}>
									<TextField
										label="Name"
										size="small"
										fullWidth
										autoFocus
										autoComplete="off"
										placeholder="Specify a name"
										// Reserves the helper line the other two fields have, so
										// the three blocks keep the same height and spacing.
										helperText=" "
										value={name}
										onChange={(e) => setName(e.target.value)}
									/>
								</Grid2>
							) : null}

							<Grid2 size={12}>
								<TextField
									label="Limits"
									size="small"
									fullWidth
									required
									type="number"
									placeholder="%"
									value={limitsPercentage}
									error={limitsPercentage.trim() !== "" && !isLimitsValid}
									helperText={
										limitsPercentage.trim() === ""
											? "Tolerance band around the measured R-Factor, in percent"
											: !isLimitsValid
												? "Enter a percentage greater than 0 and up to 100"
												: " "
									}
									onChange={(e) => setLimitsPercentage(e.target.value)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>

					<Box sx={{ p: 2 }}>
						<FooterModalButtons
							isSubmitLoading={buildMutation.isLoading}
							disabled={!canSubmit}
							onHide={(proceed) => {
								if (!proceed) onHide(false);
							}}
							buttonLabel="Accept"
						/>
					</Box>
				</form>
			</MesfModal>

			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
